"use client";

import { useEffect, useRef, useState } from "react";
import { useCookies } from "react-cookie";
import { useSearchParams } from "next/navigation";
import { PostHog, usePostHog } from "posthog-js/react";

import { useAuth } from "@auth/client-sdk-react";
import { useAPI } from "apps/website/hooks/useAPI";
import {
  buildGtmDataUA,
  buildGtmDataGA4,
} from "apps/website/trackers/utils/gtm-builders";
import {
  fetchPostCheckoutChargeWithRetry,
} from "apps/website/utils/fetch-post-checkout-charge";
import {
  loadMentionMeForPurchase,
} from "apps/website/utils/load-mention-me-for-purchase";
import {
  IGetPostCheckoutChargesResponse,
  IPostCheckoutCharge,
} from "@api-clients/charges";

import {
  RC_PURCHASED,
  pushToDataLayer,
} from "../trackers/triggers/handlers/gtm";

declare global {
  interface Window {
    dataLayer?: unknown[];
  }
}

function trackPurchaseToAnalytics(
  firstCharge: IPostCheckoutCharge,
  customerId: string,
  flow: string | undefined,
  cookies: Record<string, unknown>,
  setCookie: (name: string, value: string, options: { path: string }) => void,
  removeCookie: (name: string, options: { path: string }) => void,
  posthog: PostHog,
): boolean {

  const transactionId = firstCharge.chargeId.toString();
  const trackingAlreadySent = cookies[transactionId];

  if (!cookies[RC_PURCHASED] || trackingAlreadySent) {
    return false;
  }

  const gtmData = buildGtmDataUA(firstCharge, customerId, flow);
  const gtmDataGA4 = buildGtmDataGA4(firstCharge);

  setCookie(transactionId, "true", { path: "/" });

  pushToDataLayer(gtmData, "failed to push gtmData");
  pushToDataLayer({ ecommerce: null }, "failed to clear ecommerce");
  pushToDataLayer(gtmDataGA4, "failed to push gtmDataGA4");

  posthog.capture("purchase", gtmDataGA4.ecommerce);
  removeCookie(RC_PURCHASED, { path: "/" });

  return true;
}

export function useTrackSuccessfulPurchase(
  {
    inFlow,
    deliveryDateSetter,
    catNamesSetter,
    doNotShowMentionMePopup,
  }: {
    inFlow?: string,
    deliveryDateSetter?: (date: (string | undefined)) => void,
    catNamesSetter?: (catNames: (string | undefined)) => void,
    doNotShowMentionMePopup?: boolean,
  } = {},
) {
  const [ chargeData, setChargeData ] = useState<IGetPostCheckoutChargesResponse>();
  const [ katkinCustomerId, setKatkinCustomerId ] = useState<string | null>();
  const [ isLoading, setIsLoading ] = useState(true);
  const searchParams = useSearchParams();
  const api = useAPI();
  const auth = useAuth();
  const [
    cookies,
    setCookie,
    removeCookie,
  ] = useCookies();
  const [ logPurchaseHasFired, setLogPurchaseHasFired ] = useState(false);
  const isFetchingRef = useRef(false);

  const posthog = usePostHog();
  const rcPurchasedCookie = cookies[RC_PURCHASED];

  useEffect(() => {
    if (globalThis.window !== undefined) {
      globalThis.window.dataLayer = globalThis.window.dataLayer || [];
    }
  }, []);

  useEffect(() => {
    if (!cookies || !posthog || !globalThis.window?.dataLayer) {
      return;
    }

    if (chargeData) {
      setIsLoading(false);
      return;
    }

    async function logPurchase() {
      const getKatkinCustomerId = searchParams.get("katkinCustomerId");
      const flow = searchParams.get("flow");
      setKatkinCustomerId(getKatkinCustomerId);

      if (!getKatkinCustomerId || Array.isArray(getKatkinCustomerId)) {
        console.error("no katkinCustomerId provided. katkinCustomerId=", katkinCustomerId);
        setIsLoading(false);
        return;
      }

      if (Array.isArray(flow)) {
        console.error("multiple flows provided. flow=", flow);
        setIsLoading(false);
        return;
      }

      if (isFetchingRef.current) {
        return;
      }

      isFetchingRef.current = true;
      try {
        const res = await fetchPostCheckoutChargeWithRetry(getKatkinCustomerId, api);
        if (res) {
          setChargeData(res);
        }
      } finally {
        isFetchingRef.current = false;
        setIsLoading(false);
      }
    }

    void logPurchase();
    // We don't want auth to be in the dependencies, since that will cause the script to rerun when the user logs in.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    rcPurchasedCookie,
    inFlow,
    searchParams,
    posthog,
    chargeData,
  ]);

  useEffect(() => {

    const effectLogPurchase = async (
      data: IGetPostCheckoutChargesResponse,
      customerId: string,
    ) => {
    /**
       * The response from the Charges Api returns two charges: the first is the checkout charge
       * and the second is any cross-sell charge they have, from the post-checkout cross-sell page.
       * However, at the moment, we only track the first charge, which is the checkout charge.
       * At a good moment in the future, we should probably destructure the "secondCharge"
       * off the response and use it to track the cross-sell charges.
       * Please note that if we want to track the cross-sell charges,
       * we'd need to make the appropriate changes to the GTM-builders, as some values are
       * hardcoded.
     */
      const { firstCharge } = data;
      const flow = inFlow || searchParams.get("flow") || undefined;

      deliveryDateSetter?.(firstCharge.deliveryDate);
      catNamesSetter?.(firstCharge.catNames);

      loadMentionMeForPurchase(
        firstCharge,
        customerId,
        !(doNotShowMentionMePopup ?? false),
      );

      if (data.authData.accessToken && data.authData.refreshToken) {
        console.log("Forcing log in, useTrackSuccessfulPurchase");
        await auth.forceLogin({
          access_token: data.authData.accessToken,
          refresh_token: data.authData.refreshToken,
          expires_in: data.authData.expiresIn,
          token_type: "Bearer",
        }, true);
      }

      const tracked = trackPurchaseToAnalytics(
        firstCharge,
        customerId,
        flow,
        cookies,
        setCookie,
        removeCookie,
        posthog,
      );

      if (tracked) {
        setLogPurchaseHasFired(true);
      }
    };

    if (chargeData && katkinCustomerId && !logPurchaseHasFired) {
      void effectLogPurchase(chargeData, katkinCustomerId);
    }
  }, [ chargeData?.firstCharge ]);

  return {
    firstOrder: chargeData?.firstCharge,
    isLoading,
  };

}
